import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Badge } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import axios from 'axios';

const GymWorkoutPlans = () => {
  const [plans, setPlans] = useState([]);
  const [members, setMembers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    memberId: '', planName: '', description: '', exercises: '', dietPlan: '', trainerId: ''
  });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchMembers();
    fetchTrainers();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get('/api/gym/workout-plans');
      setPlans(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get('/api/gym/members', { params: { status: 'active' } });
      setMembers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTrainers = async () => {
    try {
      const res = await axios.get('/api/gym/trainers');
      setTrainers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        memberId: parseInt(formData.memberId),
        trainerId: formData.trainerId ? parseInt(formData.trainerId) : null
      };
      if (editId) {
        await axios.put(`/api/gym/workout-plans/${editId}`, data);
      } else {
        await axios.post('/api/gym/workout-plans', data);
      }
      setShowModal(false);
      setEditId(null);
      setFormData({ memberId: '', planName: '', description: '', exercises: '', dietPlan: '', trainerId: '' });
      fetchPlans();
    } catch (err) {
      console.error(err);
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = (plan) => {
    setFormData(plan);
    setEditId(plan.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this plan?')) {
      try {
        await axios.delete(`/api/gym/workout-plans/${id}`);
        fetchPlans();
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col><h2>💪 Workout Plans</h2></Col>
        <Col xs="auto">
          <Button variant="success" onClick={() => { setShowModal(true); setEditId(null); setFormData({ memberId: '', planName: '', description: '', exercises: '', dietPlan: '', trainerId: '' }); }}>
            <FaPlus className="me-2" /> Create Plan
          </Button>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Member</th>
                <th>Plan Name</th>
                <th>Exercises</th>
                <th>Diet Plan</th>
                <th>Trainer</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <td><strong>{plan.member?.name}</strong></td>
                  <td>{plan.planName}</td>
                  <td><small>{plan.exercises?.substring(0, 50)}...</small></td>
                  <td><small>{plan.dietPlan?.substring(0, 50) || 'N/A'}...</small></td>
                  <td>{plan.trainer?.name || 'N/A'}</td>
                  <td><Badge bg="success">{plan.status}</Badge></td>
                  <td>
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEdit(plan)}><FaEdit /></Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(plan.id)}><FaTrash /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editId ? 'Edit' : 'Create'} Workout Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Select Member *</Form.Label>
              <Form.Select required value={formData.memberId} onChange={(e) => setFormData({ ...formData, memberId: e.target.value })}>
                <option value="">Choose Member</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Plan Name *</Form.Label>
              <Form.Control required value={formData.planName} onChange={(e) => setFormData({ ...formData, planName: e.target.value })} placeholder="e.g., Weight Loss Program" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Exercises *</Form.Label>
              <Form.Control as="textarea" rows={4} required value={formData.exercises} onChange={(e) => setFormData({ ...formData, exercises: e.target.value })} placeholder="Day 1: Chest - Bench Press 3x10, Push-ups 3x15..." />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Diet Plan</Form.Label>
              <Form.Control as="textarea" rows={3} value={formData.dietPlan} onChange={(e) => setFormData({ ...formData, dietPlan: e.target.value })} placeholder="Breakfast: Oats, Lunch: Chicken..." />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Trainer</Form.Label>
              <Form.Select value={formData.trainerId} onChange={(e) => setFormData({ ...formData, trainerId: e.target.value })}>
                <option value="">Choose Trainer (Optional)</option>
                {trainers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Button type="submit" variant="success">Save Plan</Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default GymWorkoutPlans;
